import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserDropdown } from "@/components/UserDropdown";
import { UserSelector } from "@/components/UserSelector";
import { MultiSelectOptionsInput } from "./MultiSelectOptionsInput";
import { AttachmentsField } from "./AttachmentsField";
import type {
  CustomFieldProperty,
  CustomFieldValues,
} from "@forgepoint/shared";
import {
  getCustomFieldOptions,
  isAttachmentsField,
  isMultiSelectField,
} from "@forgepoint/shared";
import { cn } from "@/lib/utils";

/** Sentinel value for the "clear selection" option. Uses a null byte prefix so it can never collide with user-defined enum values. */
const CLEAR_SENTINEL = "\0__clear__";

/** Props for the CustomFieldControl component. */
interface CustomFieldControlProps {
  fieldName: string;
  prop: CustomFieldProperty;
  value: CustomFieldValues[string] | undefined;
  onChange: (fieldName: string, value: CustomFieldValues[string]) => void;
  disabled?: boolean;
  size?: "default" | "sm";
  inputId?: string;
}

/**
 * Renders the edit control for a single custom field, dispatching on the
 * property's type and format (select, multi-select, user, multi-user,
 * attachments, textarea text, or scalar input). Label and description are
 * the caller's responsibility so each surface keeps its own layout.
 * @param fieldName - The field name, passed back through onChange
 * @param prop - The custom field property definition
 * @param value - The current field value
 * @param onChange - Callback when the field value changes
 * @param disabled - Whether the control is disabled
 * @param size - Control size for selector-style inputs
 * @param inputId - Optional id for the underlying input, for label association
 */
export function CustomFieldControl({
  fieldName,
  prop,
  value,
  onChange,
  disabled = false,
  size = "default",
  inputId,
}: CustomFieldControlProps) {
  if (prop.enum) {
    const options = prop.enum.filter((v): v is string => v !== null);
    const stringValue =
      value !== null && value !== undefined ? String(value) : "";
    return (
      <Select
        value={stringValue}
        onValueChange={(val) =>
          onChange(fieldName, val === CLEAR_SENTINEL ? null : val)
        }
        disabled={disabled}
      >
        <SelectTrigger id={inputId} className="h-8 text-sm">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            value={CLEAR_SENTINEL}
            className="text-muted-foreground italic"
          >
            None
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (isMultiSelectField(prop)) {
    return (
      <MultiSelectOptionsInput
        options={getCustomFieldOptions(prop) ?? []}
        selectedValues={Array.isArray(value) ? value : []}
        onValuesChange={(vals) =>
          onChange(fieldName, vals.length > 0 ? vals : null)
        }
        disabled={disabled}
        size={size}
      />
    );
  }

  if (prop.format === "fpUser" && prop.type[0] === "array") {
    return (
      <UserSelector
        mode="multi"
        selectedUserIds={Array.isArray(value) ? value : []}
        onUserIdsChange={(ids) =>
          onChange(fieldName, ids.length > 0 ? ids : null)
        }
        disabled={disabled}
        size={size}
      />
    );
  }

  if (isAttachmentsField(prop)) {
    return (
      <AttachmentsField
        assetIds={Array.isArray(value) ? value : []}
        onChange={(ids) => onChange(fieldName, ids.length > 0 ? ids : null)}
        disabled={disabled}
      />
    );
  }

  if (prop.format === "fpUser") {
    return (
      <UserDropdown
        userId={typeof value === "string" ? value : null}
        onChangeUserId={(id) => onChange(fieldName, id)}
        placeholder="Select a user"
        disabled={disabled}
      />
    );
  }

  if (prop.type[0] === "string" && prop.format === "fpTextarea") {
    return (
      <Textarea
        id={inputId}
        value={typeof value === "string" ? value : ""}
        disabled={disabled}
        onChange={(e) =>
          onChange(fieldName, e.target.value === "" ? null : e.target.value)
        }
        rows={3}
        className="text-sm"
      />
    );
  }

  const inputType =
    prop.type[0] === "number"
      ? "number"
      : prop.format === "date"
        ? "date"
        : "text";
  const isPercentage = prop.format === "percentage";
  const scalarValue = Array.isArray(value) ? null : (value ?? null);
  const displayValue = scalarValue === null ? "" : String(scalarValue);

  return (
    <div className="relative">
      <Input
        id={inputId}
        type={inputType}
        value={displayValue}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(fieldName, null);
            return;
          }
          if (inputType === "number") {
            const parsed = Number(raw);
            if (Number.isNaN(parsed)) {
              onChange(fieldName, raw);
              return;
            }
            onChange(
              fieldName,
              isPercentage ? Math.min(100, Math.max(0, parsed)) : parsed,
            );
            return;
          }
          onChange(fieldName, raw);
        }}
        className={cn("h-8 text-sm", isPercentage && "pr-7")}
        placeholder={inputType === "number" ? "0" : ""}
        min={isPercentage ? 0 : undefined}
        max={isPercentage ? 100 : undefined}
      />
      {isPercentage && (
        <span
          className={cn(
            "pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground",
            size === "sm" ? "text-xs" : "text-sm",
          )}
        >
          %
        </span>
      )}
    </div>
  );
}
